import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
} from "antd";
import styled from "@emotion/styled";
import { TopBar } from "screens/employer/create-employer/top-bar";
import { changeArrayToStringInObject, useDocumentTitle } from "utils";
import { Profiler } from "components/profiler";
import { useAddEmployer } from "utils/employer";
import { District } from "components/district";
import { AdminSelect } from "components/admin-select";
import PageContainer from "../../../../components/layout/PageContainer";

export const CreateIndividualEmployerScreen = () => {
  useDocumentTitle("create an individual employer", false);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { mutate, isLoading, isError, isSuccess, data } = useAddEmployer();

  useEffect(() => {
    if (isSuccess && data) {
      // @ts-ignore
      navigate(`/employer/${data?.uuid}`);
    }
  }, [isSuccess]);

  const onSubmit = async (values?: any) => {
    await mutate(changeArrayToStringInObject(values));
  };

  return (
    <Profiler id="創建個人僱主">
      <PageContainer>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{ type: 0 }}
        >
          <TopBar title="create an individual employer" isLoading={isLoading} />
          <Form.Item name="type" hidden>
            <Input />
          </Form.Item>
          <ContentContainer>
            <FormCard>
              <CardTitle>employer information</CardTitle>
              <Row gutter={[16, 4]}>
                <Col span={12}>
                  <Form.Item
                    name="contactPerson"
                    label="contact name"
                    rules={[{ required: true, message: "please enter a contact name" }]}
                  >
                    <Input placeholder="contact name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="relation"
                    label="relationship with patients"
                    rules={[{ required: true, message: "please enter the relationship with the patient" }]}
                  >
                    <Input placeholder="relationship with patients" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="code" label="community voucher number">
                    <Input placeholder="community voucher number" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="codePercentage" label="community voucher percentage">
                    <InputNumber
                      placeholder="community voucher percentage"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </FormCard>

            <FormCard>
              <CardTitle>contact information</CardTitle>
              <Row gutter={[16, 4]}>
                <Col span={12}>
                  <Form.Item
                    name="contactNumber"
                    label="contact number"
                    rules={[{ required: true, message: "please enter your contact number" }]}
                  >
                    <Input placeholder="contact number" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="email" label="email">
                    <Input placeholder="email" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="district"
                    label="regions served"
                    rules={[{ required: true, message: "please select a service region" }]}
                  >
                    <District />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="address"
                    label="full service address"
                    rules={[{ required: true, message: "please enter the full service address" }]}
                  >
                    <Input placeholder="full service address" />
                  </Form.Item>
                </Col>
              </Row>
            </FormCard>

            <FormCard>
              <CardTitle>UniCare Remark</CardTitle>
              <Row gutter={[16, 4]}>
                <Col span={12}>
                  <Form.Item
                    name="personInCharge"
                    label="persion in charge"
                    rules={[{ required: true, message: "please select the person in charge" }]}
                  >
                    <AdminSelect placeholder="person in charge" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="remark" label="remark">
                    <Input.TextArea
                      placeholder="remark"
                      autoSize={{ minRows: 2, maxRows: 6 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </FormCard>

            <FormCard>
              <CardTitle>patient information</CardTitle>
              <Row gutter={[16, 4]}>
                <Col span={12}>
                  <Form.Item
                    name="patientName"
                    label="patient's name"
                    rules={[{ required: true, message: "please enter the patient's name" }]}
                  >
                    <Input placeholder="patient's name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="patientIdCardNum"
                    label="id card/passport number"
                    rules={[
                      { required: true, message: "please enter your ID card/passport number" },
                    ]}
                  >
                    <Input placeholder="id card number/passport number" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="patientGender"
                    label="gender"
                    rules={[{ required: true, message: "please enter your gender" }]}
                  >
                    <Radio.Group>
                      <Radio value={1}>females</Radio>
                      <Radio value={0}>male</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="patientAge"
                    label="age"
                    rules={[{ required: true, message: "please enter your age" }]}
                  >
                    <InputNumber min={0} max={150} placeholder="age" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="patientHeight"
                    label="height(cm)"
                    rules={[{ required: true, message: "please enter your height" }]}
                  >
                    <InputNumber min={0} max={300} placeholder="height(cm)" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="patientWeight"
                    label="weight(kg)"
                    rules={[{ required: true, message: "please enter your weight" }]}
                  >
                    <InputNumber min={0} max={1000} placeholder="weight(kg)" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="patientDisease" label="disease">
                    <Checkbox.Group>
                      <Row>
                        <Col span={6}>
                          <Checkbox value="stroke">stroke</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="diabetes">diabetes</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="hypertension">hypertension</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="heart disease">heart disease</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="hepatitis">肝炎</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="contagious disease">contagious disease</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="kidney failure">kidney failure</Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="cancer" label="cancer">
                    <Input placeholder="please specify the organ/position" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="otherDisease" label="other diseases">
                    <Input placeholder="please specify" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="throatPipe" label="throat pipe">
                    <Checkbox.Group>
                      <Row>
                        <Col span={6}>
                          <Checkbox value="stomach tube">stomach tube</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="urinate tube">urinate tube</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="PEG">PEG</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="pneumaticostomy">pneumaticostomy</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="oxygen gas tube">oxygen gas tube</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="wash the belly tube">wash the belly tube</Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="otherPipe" label="other tube">
                    <Input placeholder="please specify" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="bodyCondition"
                    label="physical condition"
                    rules={[{ required: true, message: "please enter your medical condition" }]}
                  >
                    <Radio.Group>
                      <Radio value="stable and healthy">stable and healthy</Radio>
                      <Radio value="the condition is stable">the condition is stable</Radio>
                      <Radio value="the situation is unstable">the situation is unstable</Radio>
                      <Radio value="the situation is critical">the situation is critical</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="mentalCondition"
                    label="psychiatric condition"
                    rules={[{ required: true, message: "please enter your mental condition" }]}
                  >
                    <Radio.Group>
                      <Radio value="sober">sober</Radio>
                      <Radio value="drowsiness/sluggish">drowsiness/sluggish</Radio>
                      <Radio value="confusion/the answer is confused">confusion/the answer is confused</Radio>
                      <Radio value="coma/can't respond">coma/can't respond</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="ability"
                    label="ability to perform activities"
                    rules={[{ required: true, message: "please enter mobility" }]}
                  >
                    <Radio.Group>
                      <Space direction="vertical">
                        <Radio value="get out of bed and move around often">get out of bed and move around often</Radio>
                        <Radio value="need assistance to walk">need assistance to walk</Radio>
                        <Radio value="seated position only(wheelchair)">seated position only(wheelchair)</Radio>
                        <Radio value="can't move(inability to move limbs or bodies, poses cannot be changed)">
                          "can't move(inability to move limbs or bodies, poses cannot be changed)"
                        </Radio>
                        <Radio value="very restrictive(assistance is required to change positions, mobility of limbs or bodies is carried out with assistance)">
                          very restrictive(assistance is required to change positions, mobility of limbs or bodies is carried out with assistance)
                        </Radio>
                        <Radio value=" a little restricted(able to control and move limbs or bodies, but there are limitations, assistance is required to change positions)">
                          a little restricted(able to control and move limbs or bodies, but there are limitations, assistance is required to change positions)
                        </Radio>
                        <Radio value="there is no limit(often can move limbs or bodies independently)">
                          there is no limit(often can move limbs or bodies independently)
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="incontinence"
                    label="incontinent"
                    rules={[{ required: true, message: "please enter incontinence" }]}
                  >
                    <Radio.Group>
                      <Space direction="vertical">
                        <Radio value="nope">nope</Radio>
                        <Radio value="incontinence(inability to control bowel and bowel movements，about 7 / 10 times a day)">
                          incontinence(inability to control bowel and bowel movements，about 7 / 10 times a day)
                        </Radio>
                        <Radio value=" usually urinary incontinence(pee about 3-6 urinary incontinence/days，or in the past 24-hour diarrhea)">
                          usually urinary incontinence(pee about 3-6 urinary incontinence/days，or in the past 24-hour diarrhea)
                        </Radio>
                        <Radio value=" sometime(about 1-2 fecal or urinary incontinence/day, but not because of diuretics/cathartic，have a urine tube but can restrain stool)">
                          sometime(about 1-2 fecal or urinary incontinence/day, but not because of diuretics/cathartic，have a urine tube but can restrain stool)
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="mainCare" label="primary care">
                    <Checkbox.Group>
                      <Row>
                        <Col span={5}>
                          <Checkbox value="wash your belly">wash your belly</Checkbox>
                        </Col>
                        <Col span={5}>
                          <Checkbox value="blood washing">blood washing</Checkbox>
                        </Col>
                        <Col span={5}>
                          <Checkbox value="shower/wipe your body">shower/wipe your body</Checkbox>
                        </Col>
                        <Col span={5}>
                          <Checkbox value="turn">turn</Checkbox>
                        </Col>
                        <Col span={4}>
                          <Checkbox value="rice bowl">rice bowl</Checkbox>
                        </Col>
                        <Col span={5}>
                          <Checkbox value="phlegm">phlegm</Checkbox>
                        </Col>
                        <Col span={5}>
                          <Checkbox value="wash the wound">wash the wound</Checkbox>
                        </Col>
                        <Col span={5}>
                          <Checkbox value="diaper changes">diaper changes</Checkbox>
                        </Col>
                        <Col span={5}>
                          <Checkbox value="simple movement">simple movement</Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="injection" label="needles">
                    <Input placeholder="indicate the name of the drug" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="oralDrugs" label="oral medications">
                    <Input placeholder="indicate the name of the drug" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="otherNursing" label="other cares">
                    <Input placeholder="please specify" />
                  </Form.Item>
                </Col>
              </Row>
            </FormCard>
          </ContentContainer>
        </Form>
      </PageContainer>
    </Profiler>
  );
};


const ContentContainer = styled.div`
  padding: 3.2rem;
`;

const FormCard = styled.div`
  padding: 2.4rem;
  background-color: #ffffff;
  margin-bottom: 2.4rem;
`;

const CardTitle = styled.div`
  color: #3c3e42;
  font-size: 20px;
  font-weight: bold;
  padding-bottom: 10px;
`;

const InputLabel = styled.div`
  color: #3c3e42;
  font-size: 20px;
  font-weight: bold;
`;
